import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { ArrowLeft, Download, Trash2, Calendar, Tag, Shield } from 'lucide-react';

const DocumentDetails = () => {
    const { id } = useParams();
    const { token } = useAuth();
    const navigate = useNavigate();
    const [docData, setDocData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchDoc = async () => {
            try {
                const response = await axios.get('http://localhost:4000/api/documents', {
                    headers: { Authorization: `Bearer ${token}` },
                });
                const foundDoc = response.data.data.find((d) => d._id === id);
                setDocData(foundDoc);
            } catch (error) {
                console.error(error);
            } finally {
                setLoading(false);
            }
        };
        fetchDoc();
    }, [id, token]);

    const handleDownload = async () => {
        if (!docData) return;
        try {
            const response = await axios.get(`http://localhost:4000/api/documents/${docData._id}/download`, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob',
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${docData.title}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            console.error('Download failed', error);
        }
    };

    if (loading) return <Layout><div className="p-10">Loading...</div></Layout>;
    if (!docData) return <Layout><div className="p-10">Document not found</div></Layout>;

    return (
        <Layout>
            <div className="max-w-4xl mx-auto">
                <button
                    onClick={() => navigate(-1)}
                    className="flex items-center text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white mb-6"
                >
                    <ArrowLeft className="h-5 w-5 mr-2" />
                    Back to Documents
                </button>

                <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
                    <div className="px-6 py-5 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{docData.title}</h1>
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mt-2">
                                {docData.category}
                            </span>
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={handleDownload}
                                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            >
                                <Download className="h-4 w-4 mr-2" />
                                Download
                            </button>
                            <button className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500">
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete
                            </button>
                        </div>
                    </div>

                    <div className="px-6 py-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Metadata</h3>
                            <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                                <div className="sm:col-span-1">
                                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Uploaded On</dt>
                                    <dd className="mt-1 text-sm text-gray-900 dark:text-gray-200 flex items-center">
                                        <Calendar className="h-4 w-4 mr-1 text-gray-400" />
                                        {new Date(docData.created_at).toLocaleDateString()}
                                    </dd>
                                </div>
                                <div className="sm:col-span-1">
                                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">File Size</dt>
                                    <dd className="mt-1 text-sm text-gray-900 dark:text-gray-200">
                                        {(docData.size / 1024 / 1024).toFixed(2)} MB
                                    </dd>
                                </div>
                                <div className="sm:col-span-1">
                                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Sensitivity</dt>
                                    <dd className="mt-1 text-sm text-gray-900 dark:text-gray-200 flex items-center">
                                        <Shield className="h-4 w-4 mr-1 text-gray-400" />
                                        {docData.sensitivity_level}
                                    </dd>
                                </div>
                                <div className="sm:col-span-2">
                                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Tags</dt>
                                    <dd className="mt-1 text-sm text-gray-900 dark:text-gray-200 flex flex-wrap gap-2">
                                        {docData.tags && docData.tags.length > 0 ? (
                                            docData.tags.map((tag, idx) => (
                                                <span key={idx} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                                                    <Tag className="h-3 w-3 mr-1" />
                                                    {tag}
                                                </span>
                                            ))
                                        ) : (
                                            <span className="text-gray-400">No tags</span>
                                        )}
                                    </dd>
                                </div>
                            </dl>
                        </div>

                        <div>
                            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Preview</h3>
                            <div className="border border-gray-200 dark:border-gray-700 rounded-lg h-64 flex items-center justify-center bg-gray-50 dark:bg-gray-900">
                                <p className="text-gray-500 dark:text-gray-400">Preview not available (Encrypted)</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default DocumentDetails;
